import { createMeetGrid } from "@thangdevalone/meeting-grid-layout-core";

const DEFAULT_PARTICIPANT_ADAPTIVE_GRID_ASPECT_RATIO = "16:9";
const PARTICIPANT_ADAPTIVE_GRID_FLOAT_INSET_PX = 12;
const PARTICIPANT_ADAPTIVE_GRID_TWO_ITEM_PINNED_MAIN_RATIO = 0.64;
const PARTICIPANT_ADAPTIVE_GRID_TWO_ITEM_PINNED_STACK_MAX_WIDTH_PX = 640;
function toFiniteNumber(value, fallback = 0) {
  return Number.isFinite(value) ? value : fallback;
}

function toSafeCount(count) {
  return Math.max(0, Math.floor(toFiniteNumber(count)));
}

function toSafeDimensions(dimensions = {}) {
  return {
    width: Math.max(0, toFiniteNumber(dimensions.width)),
    height: Math.max(0, toFiniteNumber(dimensions.height))
  };
}

function toCssPixelValue(value) {
  return `${toFiniteNumber(value)}px`;
}

function getParticipantAdaptiveGridOptions({
  aspectRatio = DEFAULT_PARTICIPANT_ADAPTIVE_GRID_ASPECT_RATIO,
  count,
  currentPage = 0,
  currentVisiblePage = 0,
  dimensions,
  disableFloat,
  floatBreakpoints,
  floatHeight,
  floatWidth,
  gap = 12,
  itemAspectRatios,
  layoutMode = "gallery",
  maxItemsPerPage,
  maxVisible,
  othersPosition,
  pinOnly,
  pinnedIndex,
  pipIndex,
  twoItemPinnedSplit
}) {
  const safeCount = toSafeCount(count);
  const safeDimensions = toSafeDimensions(dimensions);
  const safePinnedIndex =
    Number.isInteger(pinnedIndex) && pinnedIndex >= 0 && pinnedIndex < safeCount ? pinnedIndex : undefined;
  const effectiveLayoutMode = layoutMode === "spotlight" ? "spotlight" : "gallery";
  const shouldUseSingleFill = safeCount === 1 && safePinnedIndex === undefined;

  return {
    aspectRatio,
    count: safeCount,
    currentPage: Math.max(0, Math.floor(toFiniteNumber(currentPage))),
    currentVisiblePage: Math.max(0, Math.floor(toFiniteNumber(currentVisiblePage))),
    dimensions: safeDimensions,
    disableFloat,
    floatBreakpoints,
    floatHeight,
    floatWidth,
    gap: Math.max(0, toFiniteNumber(gap)),
    itemAspectRatios: shouldUseSingleFill ? ["auto"] : itemAspectRatios,
    layoutMode: shouldUseSingleFill ? "spotlight" : effectiveLayoutMode,
    maxItemsPerPage,
    maxVisible,
    othersPosition,
    pinOnly,
    pinnedIndex: safePinnedIndex,
    pipIndex,
    twoItemPinnedSplit
  };
}

function createTwoItemPinnedSplitGrid(gridOptions) {
  const { dimensions, gap, pinnedIndex } = gridOptions;
  const itemIndices = gridOptions.twoItemPinnedSplitItemIndices || [0, 1];
  const shouldStack = dimensions.width <= PARTICIPANT_ADAPTIVE_GRID_TWO_ITEM_PINNED_STACK_MAX_WIDTH_PX;
  const availablePrimarySize = Math.max(0, (shouldStack ? dimensions.height : dimensions.width) - gap);
  const mainPrimarySize = Math.round(availablePrimarySize * PARTICIPANT_ADAPTIVE_GRID_TWO_ITEM_PINNED_MAIN_RATIO);
  const secondaryPrimarySize = Math.max(0, availablePrimarySize - mainPrimarySize);
  const mainPosition = { top: 0, left: 0 };
  const secondaryPosition = shouldStack
    ? { top: mainPrimarySize + gap, left: 0 }
    : { top: 0, left: mainPrimarySize + gap };
  const getItemGeometry = index => {
    const isMain = index === pinnedIndex;

    return {
      dimensions: shouldStack
        ? {
            width: dimensions.width,
            height: isMain ? mainPrimarySize : secondaryPrimarySize
          }
        : {
            width: isMain ? mainPrimarySize : secondaryPrimarySize,
            height: dimensions.height
          },
      position: isMain ? mainPosition : secondaryPosition
    };
  };
  const items = itemIndices.map(index => {
    const itemGeometry = getItemGeometry(index);

    return {
      ...itemGeometry,
      hiddenCount: 0,
      index,
      isFloat: false,
      isLastVisibleOther: index !== pinnedIndex,
      isMain: index === pinnedIndex,
      isVisible: true
    };
  });

  return {
    cols: shouldStack ? 1 : 2,
    floatDimensions: undefined,
    floatIndex: undefined,
    hiddenCount: 0,
    items,
    layoutMode: "gallery",
    pagination: gridOptions.twoItemPinnedSplitPagination || {
      enabled: false,
      currentPage: 0,
      totalPages: 1,
      itemsOnPage: 2,
      startIndex: 0,
      endIndex: 1
    },
    rows: shouldStack ? 2 : 1
  };
}

function getTwoItemPinnedSplitGridOptions(gridOptions) {
  if (!gridOptions.twoItemPinnedSplit || gridOptions.pinnedIndex === undefined) {
    return null;
  }

  if (gridOptions.count === 2) {
    return {
      ...gridOptions,
      twoItemPinnedSplitItemIndices: [0, 1]
    };
  }

  if (!gridOptions.maxVisible || gridOptions.count < 2) {
    return null;
  }

  const secondaryIndices = Array.from({ length: gridOptions.count }, (_item, index) => index).filter(
    index => index !== gridOptions.pinnedIndex
  );
  const safeMaxVisible = Math.max(1, Math.floor(gridOptions.maxVisible));
  const totalPages = Math.ceil(secondaryIndices.length / safeMaxVisible);
  const currentPage = Math.min(gridOptions.currentVisiblePage, Math.max(0, totalPages - 1));
  const startIndex = currentPage * safeMaxVisible;
  const pageSecondaryIndices = secondaryIndices.slice(startIndex, startIndex + safeMaxVisible);

  if (pageSecondaryIndices.length !== 1) {
    return null;
  }

  return {
    ...gridOptions,
    twoItemPinnedSplitItemIndices: [gridOptions.pinnedIndex, pageSecondaryIndices[0]],
    twoItemPinnedSplitPagination: {
      enabled: totalPages > 1,
      currentPage,
      totalPages,
      itemsOnPage: pageSecondaryIndices.length,
      startIndex,
      endIndex: startIndex + pageSecondaryIndices.length
    }
  };
}

function getParticipantAdaptiveGridItemPosition(grid, index, containerDimensions) {
  if (grid.floatIndex === index && grid.floatDimensions) {
    return {
      left: Math.max(
        0,
        containerDimensions.width - grid.floatDimensions.width - PARTICIPANT_ADAPTIVE_GRID_FLOAT_INSET_PX
      ),
      top: Math.max(
        0,
        containerDimensions.height - grid.floatDimensions.height - PARTICIPANT_ADAPTIVE_GRID_FLOAT_INSET_PX
      )
    };
  }

  return grid.getPosition(index);
}

function createParticipantAdaptiveGridItem(grid, index, containerDimensions) {
  const dimensions = grid.getItemDimensions(index);
  const position = getParticipantAdaptiveGridItemPosition(grid, index, containerDimensions);

  return {
    dimensions,
    hiddenCount: grid.hiddenCount,
    index,
    isFloat: grid.floatIndex === index,
    isLastVisibleOther: grid.getLastVisibleOthersIndex() === index,
    isMain: grid.isMainItem(index),
    isVisible: grid.isItemVisible(index),
    position
  };
}

function getParticipantAdaptiveItemsVerticalBounds(items) {
  return items.reduce(
    (bounds, item) => ({
      bottom: Math.max(bounds.bottom, item.position.top + item.dimensions.height),
      top: Math.min(bounds.top, item.position.top)
    }),
    {
      bottom: Number.NEGATIVE_INFINITY,
      top: Number.POSITIVE_INFINITY
    }
  );
}

function centerParticipantAdaptiveSideItems(items, gridOptions) {
  if (
    gridOptions.pinnedIndex === undefined ||
    (gridOptions.othersPosition !== "left" && gridOptions.othersPosition !== "right")
  ) {
    return items;
  }

  const mainItem = items.find(item => item.isMain);
  const secondaryItems = items.filter(item => !item.isMain && !item.isFloat);

  if (!mainItem || secondaryItems.length === 0) {
    return items;
  }

  const secondaryBounds = getParticipantAdaptiveItemsVerticalBounds(secondaryItems);
  const secondaryHeight = secondaryBounds.bottom - secondaryBounds.top;
  const mainCenter = mainItem.position.top + mainItem.dimensions.height / 2;
  const centeredTop = mainCenter - secondaryHeight / 2;
  const offsetY = Math.max(0, centeredTop) - secondaryBounds.top;

  if (offsetY <= 0) {
    return items;
  }

  return items.map(item =>
    item.isMain || item.isFloat
      ? item
      : {
          ...item,
          position: {
            ...item.position,
            top: item.position.top + offsetY
          }
        }
  );
}

export function createParticipantAdaptiveGrid(options = {}) {
  const gridOptions = getParticipantAdaptiveGridOptions(options);
  const twoItemPinnedSplitGridOptions = getTwoItemPinnedSplitGridOptions(gridOptions);

  if (twoItemPinnedSplitGridOptions) {
    return createTwoItemPinnedSplitGrid(twoItemPinnedSplitGridOptions);
  }

  const grid = createMeetGrid(gridOptions);
  const items = [];

  for (let index = 0; index < gridOptions.count; index++) {
    if (grid.isItemVisible(index)) {
      items.push(createParticipantAdaptiveGridItem(grid, index, gridOptions.dimensions));
    }
  }
  const adjustedItems = centerParticipantAdaptiveSideItems(items, gridOptions);

  return {
    cols: grid.cols,
    floatDimensions: grid.floatDimensions,
    floatIndex: grid.floatIndex,
    hiddenCount: grid.hiddenCount,
    items: adjustedItems,
    layoutMode: grid.layoutMode,
    pagination: grid.pagination,
    rows: grid.rows
  };
}

export function getParticipantAdaptiveGridItemStyle(item) {
  return {
    "--participant-adaptive-grid-x": toCssPixelValue(item?.position?.left),
    "--participant-adaptive-grid-y": toCssPixelValue(item?.position?.top),
    "--participant-adaptive-grid-width": toCssPixelValue(item?.dimensions?.width),
    "--participant-adaptive-grid-height": toCssPixelValue(item?.dimensions?.height)
  };
}
